/**
 * The date helpers a rule — or a document-form calculation — may call.
 *
 * A port of `executor._DATE_FUNCS`, and it exists for one reason: without it,
 * `today() - {ctx.DEAL_DATE}` computes perfectly well on the server and fails
 * on the client with "Unknown function 'today'". A document whose most natural
 * calculation ("how long has this been open?") shows an error while the saved
 * record holds the right number is worse than one that cannot do it at all.
 *
 * The plan put the choice plainly: the date helpers are in the shared fixture
 * too, or they are not available to calculations — one or the other, decided
 * deliberately rather than by accident. This is that decision, made the first
 * way.
 *
 * **Every helper that yields a date returns a day-ordinal** (a plain integer,
 * Python's `date.toordinal()`), so it flows through the numeric evaluator and
 * compares against date field values exactly as the engine's does. `age()` and
 * `date_diff()` return a plain count of years/units.
 *
 * `today()` is the one thing here that is not a pure function of its arguments,
 * so the server's answer and the client's can differ across midnight. That is
 * the same window every `today()` rule in the platform already lives with, and
 * the save-time recompute is what settles it: the server's value is written.
 */

import { dateOrdinal, num, str } from './exprEval'

/** A plain calendar date, kept as its three parts to avoid time-zone drift. */
interface Ymd { y: number; m: number; d: number }

const MONTH_DAYS = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334]

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0
}

function lastDayOf(y: number, m: number): number {
  if (m === 2) return isLeap(y) ? 29 : 28
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1] ?? 30
}

function toOrdinal({ y, m, d }: Ymd): number {
  const y1 = y - 1
  let days = y1 * 365 + Math.floor(y1 / 4) - Math.floor(y1 / 100)
    + Math.floor(y1 / 400)
  days += MONTH_DAYS[m - 1]
  if (m > 2 && isLeap(y)) days += 1
  return days + d
}

/** The inverse — Python's `date.fromordinal`. */
function fromOrdinal(ordinal: number): Ymd {
  let n = Math.trunc(ordinal)
  if (!Number.isFinite(n) || n < 1) return { y: 1, m: 1, d: 1 }
  let y = Math.floor(n / 365.2425) + 1
  while (toOrdinal({ y: y + 1, m: 1, d: 1 }) <= n) y += 1
  while (toOrdinal({ y, m: 1, d: 1 }) > n) y -= 1
  n -= toOrdinal({ y, m: 1, d: 1 }) - 1
  let m = 1
  while (m < 12 && n > lastDayOf(y, m)) {
    n -= lastDayOf(y, m)
    m += 1
  }
  return { y, m, d: n }
}

function todayYmd(): Ymd {
  const now = new Date()
  return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate() }
}

/**
 * Coerce an expression value to a date — the mirror of `executor._to_date`.
 *
 * Accepts a day-ordinal (so the output of `today()` can feed another helper),
 * a `Date`, or a date-ish string. `null` for blanks and anything unparseable,
 * so a half-filled form does not blow up a rule.
 */
function toDate(value: unknown): Ymd | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'boolean') return null
  if (value instanceof Date) {
    return { y: value.getUTCFullYear(), m: value.getUTCMonth() + 1,
      d: value.getUTCDate() }
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? fromOrdinal(value) : null
  }
  const ordinal = dateOrdinal(str(value))
  return ordinal === null ? null : fromOrdinal(ordinal)
}

function asInt(value: unknown): number {
  return Math.trunc(num(value))
}

/** Shift by whole months, clamping the day (Jan-31 + 1 month → Feb-28/29). */
function addMonths(base: Ymd, n: number): Ymd {
  const total = base.m - 1 + Math.trunc(n)
  const y = base.y + Math.floor(total / 12)
  const m = ((total % 12) + 12) % 12 + 1
  return { y, m, d: Math.min(base.d, lastDayOf(y, m)) }
}

/** Shift by whole years; Feb-29 → Feb-28 in a non-leap target year. */
function addYears(base: Ymd, n: number): Ymd {
  const y = base.y + Math.trunc(n)
  return { y, m: base.m, d: Math.min(base.d, lastDayOf(y, base.m)) }
}

function normUnit(unit: unknown): string {
  return str(unit ?? 'day').trim().toLowerCase().replace(/s$/, '')
}

function shiftDate(d: unknown, n: unknown, unit: unknown = 'day')
  : number | null {
  const base = toDate(d)
  if (!base) return null
  const count = asInt(n)
  const u = normUnit(unit)
  if (u === 'day' || u === 'd') return toOrdinal(base) + count
  if (u === 'week' || u === 'wk' || u === 'w') return toOrdinal(base) + 7 * count
  if (u === 'month' || u === 'mon' || u === 'mo') {
    return toOrdinal(addMonths(base, count))
  }
  if (u === 'year' || u === 'yr' || u === 'y') {
    return toOrdinal(addYears(base, count))
  }
  throw new Error(`Unknown date unit '${str(unit)}'; use day, week, month, or year.`)
}

function dateDiff(d1: unknown, d2: unknown, unit: unknown = 'day'): number {
  const a = toDate(d1)
  const b = toDate(d2)
  if (!a || !b) return 0
  const u = normUnit(unit)
  if (u === 'day' || u === 'd') return toOrdinal(a) - toOrdinal(b)
  if (u === 'week' || u === 'wk' || u === 'w') {
    return Math.floor((toOrdinal(a) - toOrdinal(b)) / 7)
  }
  if (u === 'month' || u === 'mon' || u === 'mo') {
    return (a.y - b.y) * 12 + (a.m - b.m) - (a.d < b.d ? 1 : 0)
  }
  if (u === 'year' || u === 'yr' || u === 'y') {
    return a.y - b.y - (a.m < b.m || (a.m === b.m && a.d < b.d) ? 1 : 0)
  }
  throw new Error(`Unknown date unit '${str(unit)}'; use day, week, month, or year.`)
}

function age(dob: unknown): number {
  const d = toDate(dob)
  if (!d) return 0
  const t = todayYmd()
  return t.y - d.y - (t.m < d.m || (t.m === d.m && t.d < d.d) ? 1 : 0)
}

/** The mirror of `executor._DATE_FUNCS`. Every date-valued one is an ordinal. */
export const DATE_FUNCS: Record<string, (...a: unknown[]) => unknown> = {
  today: () => toOrdinal(todayYmd()),
  now: () => toOrdinal(todayYmd()),
  date: (y, m, d) => toOrdinal({ y: asInt(y), m: asInt(m), d: asInt(d) }),
  days_ago: (n) => toOrdinal(todayYmd()) - asInt(n),
  days_from_now: (n) => toOrdinal(todayYmd()) + asInt(n),
  weeks_ago: (n) => toOrdinal(todayYmd()) - 7 * asInt(n),
  weeks_from_now: (n) => toOrdinal(todayYmd()) + 7 * asInt(n),
  months_ago: (n) => toOrdinal(addMonths(todayYmd(), -asInt(n))),
  months_from_now: (n) => toOrdinal(addMonths(todayYmd(), asInt(n))),
  years_ago: (n) => toOrdinal(addYears(todayYmd(), -asInt(n))),
  years_from_now: (n) => toOrdinal(addYears(todayYmd(), asInt(n))),
  date_add: (d, n, unit) => shiftDate(d, n, unit ?? 'day'),
  date_sub: (d, n, unit) => shiftDate(d, -asInt(n), unit ?? 'day'),
  add_days: (d, n) => shiftDate(d, n, 'day'),
  add_months: (d, n) => shiftDate(d, n, 'month'),
  add_years: (d, n) => shiftDate(d, n, 'year'),
  date_diff: (d1, d2, unit) => dateDiff(d1, d2, unit ?? 'day'),
  age,
}
